import { useState, useEffect, useCallback } from 'react'
import { WagmiConfig, createConfig, configureChains, mainnet } from 'wagmi'
import { publicProvider } from 'wagmi/providers/public'
import { MetaMaskConnector } from 'wagmi/connectors/metaMask'
import { Box, CircularProgress, Typography } from '@mui/material'
import { COLORS } from './constants/colors'
import { TEXT } from './constants/text'
import { apiUrl } from './constants/api'

// Pages
import LandingPage from './pages/Landing'
import FeedPage from './pages/Feed'
import SignalsPage from './pages/Signals'
import WhalesPage from './pages/Whales'
import PricingPage from './pages/Pricing'
import InfoPage from './pages/Info'
import TermsPage from './pages/Terms'
import PrivacyPage from './pages/Privacy'

// Layout
import Layout from './components/layout/Layout'

// ── wagmi config ───────────────────────────────────────────
const { chains, publicClient, webSocketPublicClient } = configureChains(
  [mainnet],
  [publicProvider()]
)

const wagmiConfig = createConfig({
  autoConnect: true,
  connectors: [new MetaMaskConnector({ chains })],
  publicClient,
  webSocketPublicClient,
})

// ── Demo ticker stats (replaced by real API in production) ──
const DEMO_TICKER = {
  totalSignals: 14823,
  rugsPrevented: 342,
  whaleAlerts: 89,
  avgRugScore: 22,
  tokens: [
    { symbol: 'BONK', change: 12.4 },
    { symbol: 'WIF', change: -3.2 },
    { symbol: 'POPCAT', change: 34.5 },
    { symbol: 'MYRO', change: 8.1 },
    { symbol: 'BOME', change: -1.7 },
    { symbol: 'MEW', change: 22.3 },
    { symbol: 'SLERF', change: -8.9 },
    { symbol: 'SAMO', change: 5.6 },
  ],
}

// ── App ────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState('landing')
  const [loading, setLoading] = useState(true)
  const [tickerStats, setTickerStats] = useState(DEMO_TICKER)

  // Simulate initial load
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 800)
    return () => clearTimeout(t)
  }, [])

  // Fetch live stats (falls back to demo)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(apiUrl('/api/engine/stats'))
        if (res.ok) {
          const data = await res.json()
          setTickerStats((prev) => ({ ...prev, ...data }))
        }
      } catch {
        // keep demo data
      }
    }
    fetchStats()
    const interval = setInterval(fetchStats, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleUpgrade = useCallback(() => setPage('pricing'), [])
  const handleNavigate = useCallback((p) => {
    setPage(p)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          background: COLORS.bg.primary,
        }}
      >
        <img
          src="/assets/images/loading-logo.png"
          alt={TEXT.BUSINESS.NAME}
          style={{ height: 60, opacity: 0.9 }}
          onError={(e) => { e.target.style.display = 'none' }}
        />
        <CircularProgress size={24} sx={{ color: COLORS.gold.primary }} />
        <Typography sx={{ fontSize: '0.7rem', color: COLORS.text.muted, letterSpacing: '2px', textTransform: 'uppercase' }}>
          {TEXT.BUSINESS.TAGLINE}
        </Typography>
      </Box>
    )
  }

  const renderPage = () => {
    switch (page) {
      case 'landing':
        return <LandingPage onNavigate={handleNavigate} tickerStats={tickerStats} />
      case 'feed':
        return <FeedPage onUpgrade={handleUpgrade} />
      case 'signals':
        return <SignalsPage onUpgrade={handleUpgrade} />
      case 'whales':
        return <WhalesPage onUpgrade={handleUpgrade} />
      case 'pricing':
        return <PricingPage />
      case 'info':
        return <InfoPage />
      case 'terms':
        return <TermsPage />
      case 'privacy':
        return <PrivacyPage />
      default:
        return <LandingPage onNavigate={handleNavigate} tickerStats={tickerStats} />
    }
  }

  return (
    <WagmiConfig config={wagmiConfig}>
      <Layout page={page} onNavigate={handleNavigate}>
        {renderPage()}
      </Layout>
    </WagmiConfig>
  )
}
